import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plane, Calendar, ClipboardList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { flightApi } from '@/services/api';
import { CalendarMonth, CalendarDay, DailyEvent, FlightSegment } from '@/types';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay, parseISO } from 'date-fns';
import { zhCN } from 'date-fns/locale';

export function FlightCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState<CalendarMonth | null>(null);
  const [, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayData, setSelectedDayData] = useState<CalendarDay | null>(null);
  const isFetching = useRef(false);

  useEffect(() => {
    if (isFetching.current) return;
    fetchCalendarData();
  }, [currentDate]);

  // Helper to extract data from various response formats
  const extractData = <T,>(responseData: any): T | null => {
    if (responseData === null || responseData === undefined) {
      return null;
    }
    // If it's a wrapped response: { success, data }
    if (responseData.data !== undefined) {
      return responseData.data as T;
    }
    // Direct data
    return responseData as T;
  };

  const fetchCalendarData = async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    setLoading(true);
    try {
      const response = await flightApi.getCalendar(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1
      );
      setCalendarData(extractData<CalendarMonth>(response.data));
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day data
  const getDayData = (date: Date): CalendarDay | undefined => {
    return calendarData?.days.find((d) => {
      const serverDate = d.date.split('T')[0]; // "2025-02-01"
      const localDate = format(date, 'yyyy-MM-dd'); // "2025-02-01"
      return serverDate === localDate;
    });
  };

  // Handle day click
  const handleDayClick = (date: Date, dayData: CalendarDay | undefined) => {
    setSelectedDate(date);
    setSelectedDayData(dayData || null);
  };

  // Event icon helper
  const getEventIcon = (type: string) => {
    const icons: Record<string, string> = {
      attendance: '📝',
      leave: '🏖️',
      training: '🎓',
      meeting: '👥',
      standby: '⏰',
      other: '📌',
    };
    return icons[type] || '📌';
  };

  // Weekday headers
  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">日历视图</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToToday}>
            今天
          </Button>
          <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-lg font-medium min-w-[120px] text-center">
            {format(currentDate, 'yyyy年MM月', { locale: zhCN })}
          </span>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-sm font-medium text-gray-500 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month start */}
            {Array.from({ length: getDay(monthStart) }).map((_, index) => (
              <div key={`empty-${index}`} className="h-24" />
            ))}

            {/* Calendar days */}
            {calendarDays.map((date) => {
              const dayData = getDayData(date);
              const legs = dayData?.legs ?? [];
              const layovers = dayData?.layovers ?? [];
              const dailyEvents = dayData?.dailyEvents ?? [];
              const hasUnlinked = dayData?.hasUnlinkedLegs ?? false;
              const hasLegs = legs.length > 0;
              const hasEvents = dailyEvents.length > 0;
              const hasLayovers = layovers.length > 0;
              const isToday = isSameDay(date, new Date());
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const hasData = hasLegs || hasLayovers || hasEvents;

              return (
                <div
                  key={date.toISOString()}
                  onClick={() => handleDayClick(date, dayData)}
                  className={`relative min-h-[88px] rounded-xl border p-2 transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50/60 shadow-sm'
                      : isToday
                        ? 'border-indigo-400 bg-gradient-to-br from-indigo-50 to-white shadow-sm'
                        : hasUnlinked
                          ? 'border-orange-300 bg-gradient-to-br from-orange-50/50 to-white hover:shadow-md hover:border-orange-400'
                          : hasData
                            ? 'border-gray-200 bg-white hover:shadow-md hover:border-gray-300'
                            : 'border-gray-100 bg-gray-50/50 hover:bg-white hover:border-gray-200'
                  }`}
                >
                  {/* Date number */}
                  <div className="flex items-center justify-between mb-1.5">
                    <span
                      className={`inline-flex items-center justify-center w-6 h-6 text-sm font-semibold rounded-full ${
                        isToday
                          ? 'bg-indigo-600 text-white'
                          : isSelected
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-gray-700'
                      }`}
                    >
                      {format(date, 'd')}
                    </span>
                    {hasUnlinked && (
                      <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                      </span>
                    )}
                  </div>

                  {/* Events container */}
                  <div className="space-y-1">
                    {/* Flight legs - show each leg individually */}
                    {legs.slice(0, 2).map((leg, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-1 text-[11px] font-medium text-white bg-slate-700 px-1.5 py-0.5 rounded-md truncate shadow-sm"
                        title={`${leg.flightNo} ${leg.depCode}→${leg.arrCode}`}
                      >
                        <Plane className="w-2.5 h-2.5 shrink-0 opacity-80" />
                        <span className="truncate">
                          {leg.flightNo} {leg.depCode}→{leg.arrCode}
                        </span>
                      </div>
                    ))}
                    {legs.length > 2 && (
                      <div className="text-[10px] text-slate-500 font-medium px-0.5">
                        +{legs.length - 2} 航段
                      </div>
                    )}

                    {/* Layovers */}
                    {layovers.slice(0, 1).map((layover, idx) => (
                      <div
                        key={`layover-${idx}`}
                        className="flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded-md truncate border border-amber-200/60"
                        title={`驻外: ${layover.location}`}
                      >
                        <span className="shrink-0">🏨</span>
                        <span className="truncate">{layover.location}</span>
                        {layovers.length > 1 && (
                          <span className="shrink-0 text-amber-600">+{layovers.length - 1}</span>
                        )}
                      </div>
                    ))}

                    {/* Daily events (only if no flights/layovers) */}
                    {!hasLegs && !hasLayovers && dailyEvents.slice(0, 2).map((event, idx) => (
                      <div
                        key={`event-${idx}`}
                        className="flex items-center gap-1 text-[11px] font-medium text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded-md truncate border border-emerald-200/60"
                      >
                        <span className="shrink-0">{getEventIcon(event.eventType)}</span>
                        <span className="truncate">{event.title ?? '无标题'}</span>
                      </div>
                    ))}
                    {!hasLegs && !hasLayovers && dailyEvents.length > 2 && (
                      <div className="text-[10px] text-emerald-600 font-medium px-0.5">
                        +{dailyEvents.length - 2}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm px-1">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded bg-slate-700" />
          <span className="text-gray-600">航班</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded bg-amber-200 border border-amber-300" />
          <span className="text-gray-600">驻外</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" />
          <span className="text-gray-600">日常事项</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-indigo-600" />
          <span className="text-gray-600">今天</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-500" />
          </span>
          <span className="text-gray-600">未关联</span>
        </div>
      </div>

      {/* Detail Panel */}
      <Card className="mt-6">
        <CardHeader>
          {selectedDate ? (
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {format(selectedDate, 'yyyy年M月d日', { locale: zhCN })}
                  {' '}
                  {format(selectedDate, 'EEEE', { locale: zhCN })}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {(selectedDayData?.legs?.length || 0) + (selectedDayData?.layovers?.length || 0) + (selectedDayData?.dailyEvents?.length || 0) > 0 ? (
                    <>
                      共 {selectedDayData?.legs?.length || 0} 个航段，
                      {selectedDayData?.layovers?.length || 0} 个驻外，
                      {selectedDayData?.dailyEvents?.length || 0} 个事项
                    </>
                  ) : (
                    <>当天暂无数据</>
                  )}
                </p>
              </div>
              {selectedDayData?.hasUnlinkedLegs && (
                <span className="px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-700 border border-orange-200">
                  ⚠️ 有未关联航段
                </span>
              )}
            </div>
          ) : (
            <CardTitle>点击日历某天查看详情</CardTitle>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {!selectedDate ? (
            <div className="text-center py-12 px-6">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">点击日历上的任意日期，查看当天的航班和事项详情</p>
            </div>
          ) : ((selectedDayData?.legs?.length || 0) + (selectedDayData?.layovers?.length || 0) + (selectedDayData?.dailyEvents?.length || 0)) === 0 ? (
            <div className="text-center py-12 px-6">
              <ClipboardList className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">当天暂无航班和事项</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Legs Section */}
              {selectedDayData?.legs && selectedDayData.legs.length > 0 && (
                <div className="p-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                    <Plane className="w-4 h-4" />
                    航段 ({selectedDayData.legs.length})
                  </h3>
                  <div className="space-y-4">
                    {selectedDayData.legs.map((leg: FlightSegment) => {
                      const flightStatus = leg.flightStatus ?? '正班';
                      const depLabel = leg.depShortName || leg.depName || leg.depCode || '';
                      const arrLabel = leg.arrShortName || leg.arrName || leg.arrCode || '';
                      const depCode = leg.depCode || '';
                      const arrCode = leg.arrCode || '';
                      const signTime = leg.signTime;
                      const driveTime = leg.driveTime;
                      const hasActualTimes = leg.realDepTime || leg.realArrTime;
                      const hasEstTimes = (leg.estDepTime || leg.estArrTime) && !hasActualTimes;

                      return (
                        <div
                          key={leg.id ?? `${leg.chainId}-${leg.sequence}`}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="text-lg font-bold">
                                  {leg.flightNo || 'N/A'}
                                </span>
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {flightStatus}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-gray-600 mb-3">
                                <span className="font-medium">
                                  {depLabel}{depCode ? ` (${depCode})` : ''} → {arrLabel}{arrCode ? ` (${arrCode})` : ''}
                                </span>
                              </div>

                              <div className="text-sm text-gray-500 space-y-1">
                                <div className="flex items-center gap-4 flex-wrap">
                                  <span className="text-gray-600 font-medium">
                                    {leg.planDepTime ? format(parseISO(leg.planDepTime), 'HH:mm') : ''}
                                  </span>
                                  {leg.planDepTime && leg.planArrTime && (
                                    <span className="text-gray-300">→</span>
                                  )}
                                  <span className="text-gray-600">
                                    {leg.planArrTime ? format(parseISO(leg.planArrTime), 'HH:mm') : ''}
                                  </span>
                                  {(leg.planDepTime || leg.planArrTime) && (leg.acTypeCode || leg.acNo) && (
                                    <span className="text-gray-300">|</span>
                                  )}
                                  {leg.acTypeCode && <span>{leg.acTypeCode}</span>}
                                  {leg.acNo && <span>({leg.acNo})</span>}
                                  {(leg.acTypeCode || leg.acNo) && leg.postName && (
                                    <span className="text-gray-300">|</span>
                                  )}
                                  {leg.postName && <span>{leg.postName}</span>}
                                </div>

                                {hasActualTimes && (
                                  <div className="flex items-center gap-4 flex-wrap text-green-700">
                                    <span className="text-xs">实际:</span>
                                    {leg.realDepTime && <span>{format(parseISO(leg.realDepTime), 'HH:mm')}</span>}
                                    {leg.realDepTime && leg.realArrTime && (
                                      <span className="text-gray-300">→</span>
                                    )}
                                    {leg.realArrTime && <span>{format(parseISO(leg.realArrTime), 'HH:mm')}</span>}
                                  </div>
                                )}

                                {hasEstTimes && (
                                  <div className="flex items-center gap-4 flex-wrap text-amber-700">
                                    <span className="text-xs">预计:</span>
                                    {leg.estDepTime && <span>{format(parseISO(leg.estDepTime), 'HH:mm')}</span>}
                                    {leg.estDepTime && leg.estArrTime && (
                                      <span className="text-gray-300">→</span>
                                    )}
                                    {leg.estArrTime && <span>{format(parseISO(leg.estArrTime), 'HH:mm')}</span>}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="text-right shrink-0 ml-4">
                              {(signTime || driveTime) && (
                                <div className="text-xs text-gray-400 space-y-0.5">
                                  {signTime && (
                                    <p>签到: {format(parseISO(signTime), 'HH:mm')}</p>
                                  )}
                                  {driveTime && (
                                    <p>发车: {format(parseISO(driveTime), 'HH:mm')}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Layovers Section */}
              {selectedDayData?.layovers && selectedDayData.layovers.length > 0 && (
                <div className="p-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                    <span className="text-base">🏘️</span>
                    驻外 ({selectedDayData.layovers.length})
                  </h3>
                  <div className="space-y-3">
                    {selectedDayData.layovers.map((layover, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 bg-amber-50 border-amber-200"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-amber-900">
                              {layover.location} ({layover.locationCode})
                            </div>
                            <div className="text-sm text-amber-700 mt-1">
                              前航: {layover.fromLeg} → 后航: {layover.toLeg}
                            </div>
                          </div>
                          <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-200">
                            驻外
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Daily Events Section */}
              {selectedDayData?.dailyEvents && selectedDayData.dailyEvents.length > 0 && (
                <div className="p-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    日常事项 ({selectedDayData.dailyEvents.length})
                  </h3>
                  <div className="space-y-4">
                    {selectedDayData.dailyEvents.map((event: DailyEvent) => {
                      const eventType = event.eventType ?? 'other';
                      const title = event.title ?? '无标题';
                      const eventTypeDisplay = event.eventTypeDisplay ?? '';
                      const remarks = event.remarks ?? '';
                      const startTime = event.startTime;
                      const endTime = event.endTime;

                      return (
                        <div
                          key={event.id}
                          className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start gap-4">
                            <div className="text-2xl">{getEventIcon(eventType)}</div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold">{title}</span>
                                {eventTypeDisplay && (
                                  <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                                    {eventTypeDisplay}
                                  </span>
                                )}
                              </div>
                              {remarks && (
                                <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">{remarks}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {startTime && format(parseISO(startTime), 'yyyy-MM-dd HH:mm')}
                                  {endTime && startTime && endTime !== startTime && (
                                    <> - {format(parseISO(endTime), 'HH:mm')}</>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
